import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { userApi, teamApi, reportApi, syncApi, settingsApi, remoteApi } from '../utils/api';
import {
  Users, Building2, Loader, Settings, Download, Sparkles,
  ChevronRight, Shield, BarChart3, ClipboardList, CalendarDays, ScrollText, Upload,
  Calendar as CalendarIcon, RefreshCw, CheckCircle, AlertCircle, Clock, Save, Search, FolderOpen, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { TourGuide } from '../components/TourGuide';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from 'recharts';
import { DriveFallbackPanel } from '../components/DriveFallbackPanel';
import { AdminVacationByDepartment } from '../components/AdminVacationByDepartment';
import { AdminTeamBalancesLookup } from '../components/AdminTeamBalancesLookup';

const ADMIN_TOUR_STEPS = [
  {
    target: null,
    icon: Sparkles,
    title: 'Panel de Administración — Bienvenido',
    description: 'Aquí gestionas toda la plataforma Valoriza HR: usuarios, saldos, festivos, aprobaciones, exportaciones e informes.',
    placement: 'center',
  },
  {
    target: '[data-testid="admin-stats"]',
    title: 'Métricas globales',
    description: 'Total de empleados, managers activos y equipos configurados en la plataforma.',
    placement: 'bottom',
  },
  {
    target: '[data-testid="admin-quick-actions"]',
    title: 'Acciones rápidas',
    description: 'Accede de un clic a gestión de usuarios, aprobaciones, calendario, informes IA, festivos, importar Excel y exportar datos.',
    placement: 'top',
  },
  {
    target: '[data-testid="btn-download-guide"]',
    title: 'Guía Funcional PDF',
    description: 'Descarga un PDF completo con capturas de pantalla de todas las funcionalidades para presentar el MVP a stakeholders.',
    placement: 'bottom',
  },
  {
    target: '[data-testid="admin-chart"]',
    title: 'Distribución de roles',
    description: 'Gráfica con la distribución de empleados, managers y administradores en la organización.',
    placement: 'top',
  },
  {
    target: '[data-testid="admin-vacation-by-department"]',
    title: 'Vacaciones pendientes por departamento',
    description: 'Cuadro de mando con los días de vacaciones pendientes de disfrutar agrupados por Centro de Coste, con exportación a Excel.',
    placement: 'top',
  },
  {
    target: null,
    icon: Shield,
    title: '¡Tienes control total!',
    description: 'Recuerda: cualquier cambio queda registrado en el log de auditoría. Puedes consultarlo desde el menú de navegación.',
    placement: 'center',
  },
];

const StatCard = ({ icon: Icon, label, value, color, onClick }) => (
  <div
    className={`bg-card border border-border rounded-xl p-5 shadow-sm ${onClick ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all' : ''}`}
    onClick={onClick}
  >
    <div className={`inline-flex p-2.5 rounded-lg ${color} mb-3`}>
      <Icon className="h-5 w-5" />
    </div>
    <div className="text-2xl font-bold font-heading">{value}</div>
    <div className="text-sm text-muted-foreground mt-0.5">{label}</div>
  </div>
);

export default function AdminDashboard() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [syncLoading, setSyncLoading] = useState(false);
  const [platformSettings, setPlatformSettings] = useState(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsEdit, setSettingsEdit] = useState({});
  const [sftpDiag, setSftpDiag] = useState(null);
  const [sftpDiagLoading, setSftpDiagLoading] = useState(false);
  const [serverIp, setServerIp] = useState(null);
  const [serverIpLoading, setServerIpLoading] = useState(false);

  useEffect(() => { loadData(); loadSyncLogs(); loadSettings(); }, []);

  const loadSyncLogs = useCallback(async () => {
    try {
      const res = await syncApi.logs(5);
      setSyncLogs(res.data);
    } catch (e) { /* ignore */ }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const res = await settingsApi.get();
      setPlatformSettings(res.data);
      setSettingsEdit(res.data);
    } catch (e) { /* ignore */ }
  }, []);

  const handleSaveSettings = async () => {
    setSettingsSaving(true);
    try {
      const res = await settingsApi.update(settingsEdit);
      setPlatformSettings(res.data);
      setSettingsEdit(res.data);
    } catch (e) { console.error(e); }
    finally { setSettingsSaving(false); }
  };

  const handleSftpSync = async () => {
    setSyncLoading(true);
    try {
      await syncApi.triggerSftp();
      await loadSyncLogs();
    } catch (e) { console.error(e); }
    finally { setSyncLoading(false); }
  };

  const handleFixBalances = async () => {
    if (!window.confirm('¿Recalcular remote_used para todos los usuarios? Esto corrige los contadores de teletrabajo para mostrar solo el mes actual.')) return;
    try {
      const res = await remoteApi.fixMonthlyBalances();
      alert(`✅ ${res.data.message}\nMes: ${res.data.month} | Actualizados: ${res.data.updated}`);
    } catch (e) {
      alert('Error: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const handleSftpDiag = async () => {
    setSftpDiagLoading(true);
    setSftpDiag(null);
    try {
      const res = await syncApi.sftpDiag();
      setSftpDiag(res.data);
    } catch (e) {
      setSftpDiag({ error: e?.response?.data?.detail || e.message || 'Error desconocido' });
    } finally {
      setSftpDiagLoading(false);
    }
  };

  const handleServerIp = async () => {
    setServerIpLoading(true);
    setServerIp(null);
    try {
      const res = await syncApi.serverIp();
      setServerIp(res.data);
    } catch (e) {
      setServerIp({ error: e?.response?.data?.detail || e.message || 'Error' });
    } finally {
      setServerIpLoading(false);
    }
  };

  const loadData = async () => {
    try {
      const [usersRes, teamsRes, sumRes] = await Promise.all([
        userApi.list(),
        teamApi.list(),
        reportApi.summary()
      ]);
      setUsers(usersRes.data);
      setTeams(teamsRes.data);
      setSummary(sumRes.data);

      // Build chart data per team
      const teamMap = {};
      teamsRes.data.forEach(t => { teamMap[t.id] = t.name; });
      const roleStats = { employee: 0, manager: 0, admin: 0 };
      usersRes.data.forEach(u => { if (u.is_active) roleStats[u.role] = (roleStats[u.role] || 0) + 1; });
      setChartData([
        { name: 'Empleados', value: roleStats.employee, fill: '#00a651' },
        { name: 'Managers', value: roleStats.manager, fill: '#0ea5e9' },
        { name: 'Admins', value: roleStats.admin, fill: '#8b5cf6' },
      ]);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleExport = async () => {
    try {
      const res = await reportApi.exportExcel({ type: 'all' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `valoriza_export_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) { console.error(e); }
  };

  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const handleDownloadGuide = async () => {
    setDownloadingPdf(true);
    try {
      const res = await reportApi.downloadFunctionalGuide();
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `valoriza_hr_guia_funcional.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      console.error(e);
      alert('Error al generar el PDF. Por favor, inténtelo de nuevo.');
    }
    finally { setDownloadingPdf(false); }
  };

  const [downloadingAdminManual, setDownloadingAdminManual] = useState(false);
  const handleDownloadAdminManual = async () => {
    setDownloadingAdminManual(true);
    try {
      const res = await reportApi.downloadAdminManual();
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Manual_Administrador_Valoriza_HR.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      console.error(e);
      alert('Error al descargar el manual. Por favor, inténtelo de nuevo.');
    } finally { setDownloadingAdminManual(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader className="h-6 w-6 animate-spin text-brand" />
    </div>
  );

  const activeUsers = users.filter(u => u.is_active).length;
  const managers = users.filter(u => u.role === 'manager').length;

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading">Panel Administrador</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Vista global de la plataforma Valoriza HR</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="btn-download-admin-manual"
            onClick={handleDownloadAdminManual}
            disabled={downloadingAdminManual}
            variant="outline"
            className="gap-2"
          >
            {downloadingAdminManual ? <Loader className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Manual Admin
          </Button>
          <Button
            data-testid="btn-download-guide"
            onClick={handleDownloadGuide}
            disabled={downloadingPdf}
            variant="outline"
            className="gap-2"
          >
            {downloadingPdf ? <Loader className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Guia PDF + Capturas
          </Button>
          <Button
            data-testid="btn-export-excel"
            onClick={handleExport}
            className="bg-brand hover:bg-brand-hover text-white gap-2"
          >
            <Download className="h-4 w-4" />
            {t('export')}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats">
        <StatCard icon={Users} label="Empleados activos" value={activeUsers}
          color="bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
          onClick={() => navigate('/users')} />
        <StatCard icon={Building2} label="Equipos" value={teams.length}
          color="bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400" />
        <StatCard icon={Shield} label="Managers" value={managers}
          color="bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400" />
        <StatCard icon={ClipboardList} label="Pendientes hoy" value={summary?.total_pending || 0}
          color="bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400"
          onClick={() => navigate('/approvals')} />
      </div>

      {/* Quick access tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-quick-actions">
        {[
          {
            title: 'Gestión de Usuarios', desc: `${activeUsers} usuarios activos`,
            icon: Users, color: 'bg-blue-50 dark:bg-blue-900/20', iconColor: 'text-blue-600',
            to: '/users', testid: 'admin-users'
          },
          {
            title: t('approvals'), desc: `${summary?.total_pending || 0} pendientes`,
            icon: ClipboardList, color: 'bg-yellow-50 dark:bg-yellow-900/20', iconColor: 'text-yellow-600',
            to: '/approvals', testid: 'admin-approvals'
          },
          {
            title: t('calendar'), desc: 'Vista global del equipo',
            icon: BarChart3, color: 'bg-green-50 dark:bg-green-900/20', iconColor: 'text-green-600',
            to: '/calendar', testid: 'admin-calendar'
          },
          // Informe IA deshabilitado temporalmente (hilo "Flex - Teletrabajo duda") — descomentar para reactivar:
          // {
          //   title: t('aiReport'), desc: 'Análisis e insights IA',
          //   icon: BarChart3, color: 'bg-purple-50 dark:bg-purple-900/20', iconColor: 'text-purple-600',
          //   to: '/ai-report', testid: 'admin-ai-report'
          // },
          {
            title: 'Gestión de Festivos', desc: 'Por delegación y tipo',
            icon: CalendarDays, color: 'bg-teal-50 dark:bg-teal-900/20', iconColor: 'text-teal-600',
            to: '/holidays', testid: 'admin-holidays'
          },
          {
            title: 'Calendarios por Sede', desc: 'Clonar y editar festivos por tipo de calendario',
            icon: CalendarIcon, color: 'bg-cyan-50 dark:bg-cyan-900/20', iconColor: 'text-cyan-600',
            to: '/calendars', testid: 'admin-calendars'
          },
          {
            title: 'Importar Empleados', desc: 'Sync desde Excel/SFTP',
            icon: Upload, color: 'bg-indigo-50 dark:bg-indigo-900/20', iconColor: 'text-indigo-600',
            to: '/import', testid: 'admin-import'
          },
          {
            title: 'Ajustar Vacaciones', desc: 'Inyectar días usados por empleado (Q1 2026)',
            icon: CalendarDays, color: 'bg-emerald-50 dark:bg-emerald-900/20', iconColor: 'text-emerald-600',
            to: '/vacation-balance-admin', testid: 'admin-vacation-balance'
          },
          {
            title: 'Gestión de Vacaciones', desc: 'Grabar y cancelar periodos por empleado',
            icon: CalendarDays, color: 'bg-green-50 dark:bg-green-900/20', iconColor: 'text-green-700',
            to: '/vacation-records-admin', testid: 'admin-vacation-records'
          },
          {
            title: 'Log de Auditoría', desc: 'Registro de acciones',
            icon: ScrollText, color: 'bg-slate-50 dark:bg-slate-800/40', iconColor: 'text-slate-600',
            to: '/audit-log', testid: 'admin-audit-log'
          },
          {
            title: t('export'), desc: 'Exportar datos a Excel',
            icon: Download, color: 'bg-slate-50 dark:bg-slate-800/40', iconColor: 'text-slate-600',
            onClick: handleExport, testid: 'admin-export'
          },
          {
            title: 'Corregir Saldos Teletrabajo', desc: 'Resetear contadores al mes actual',
            icon: RefreshCw, color: 'bg-orange-50 dark:bg-orange-900/20', iconColor: 'text-orange-600',
            onClick: handleFixBalances, testid: 'admin-fix-balances'
          },
          {
            title: 'Guía Funcional PDF', desc: 'Descargar documentación completa',
            icon: Download, color: 'bg-rose-50 dark:bg-rose-900/20', iconColor: 'text-rose-600',
            onClick: handleDownloadGuide, testid: 'admin-download-guide'
          },
        ].map((item) => (
          <div
            key={item.title}
            data-testid={item.testid}
            onClick={item.onClick || (() => navigate(item.to))}
            className="bg-card border border-border rounded-xl p-5 cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group"
          >
            <div className={`inline-flex p-2.5 rounded-lg ${item.color} mb-4`}>
              <item.icon className={`h-5 w-5 ${item.iconColor}`} />
            </div>
            <h3 className="font-semibold font-heading text-sm">{item.title}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
            <ChevronRight className="h-4 w-4 text-muted-foreground/50 mt-3 group-hover:text-brand transition-colors" />
          </div>
        ))}
      </div>

      {/* Chart + Recent Users */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* User distribution chart */}
        <div className="bg-card border border-border rounded-xl p-5 shadow-sm" data-testid="admin-chart">
          <h2 className="font-semibold font-heading text-sm mb-4">Distribución de usuarios</h2>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} />
              <YAxis tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                labelStyle={{ fontWeight: 600 }}
              />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {chartData.map((entry, i) => (
                  <Cell key={`cell-${entry.name || i}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Recent users */}
        <div className="lg:col-span-2 bg-card border border-border rounded-xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold font-heading text-sm">Usuarios recientes</h2>
            <Button variant="ghost" size="sm" onClick={() => navigate('/users')} className="text-xs text-brand">
              Ver todos <ChevronRight className="h-3 w-3 ml-1" />
            </Button>
          </div>
          <div className="space-y-2">
            {users.slice(0, 6).map((u) => (
              <div key={u.id} className="flex items-center justify-between p-2.5 rounded-lg hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-brand flex items-center justify-center text-white text-xs font-bold">
                    {u.first_name?.[0]}{u.last_name?.[0]}
                  </div>
                  <div>
                    <p className="text-sm font-medium">{u.first_name} {u.last_name}</p>
                    <p className="text-xs text-muted-foreground">{u.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    u.role === 'admin' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
                    u.role === 'manager' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  }`}>
                    {t(u.role)}
                  </span>
                  {!u.is_active && <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Inactivo</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Vacation balance by department — dashboard + Excel export */}
      <AdminVacationByDepartment />

      {/* Lookup any manager/approver's full team balance (e.g. "María Folgado quiere ver a toda su gente") */}
      <AdminTeamBalancesLookup users={users} />

      {/* Tour guiado del administrador */}
      <TourGuide tourId={`admin_${user?.id || 'adm'}`} steps={ADMIN_TOUR_STEPS} enabled />

      {/* SFTP Sync Card */}
      <div className="bg-card border border-border rounded-xl p-5 shadow-sm" data-testid="sftp-sync-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-semibold font-heading text-sm">Sincronización SFTP Automática</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Ejecuta diariamente a las 06:00 UTC — descarga el CSV de empleados del día anterior
            </p>
          </div>
          <div className="flex gap-2 flex-wrap justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={handleServerIp}
              disabled={serverIpLoading}
              data-testid="btn-server-ip"
              className="gap-2"
            >
              {serverIpLoading ? <Loader className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              IP Servidor
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSftpDiag}
              disabled={sftpDiagLoading}
              data-testid="btn-sftp-diag"
              className="gap-2"
            >
              {sftpDiagLoading ? <Loader className="h-4 w-4 animate-spin" /> : <FolderOpen className="h-4 w-4" />}
              Diagnóstico SFTP
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSftpSync}
              disabled={syncLoading}
              data-testid="btn-trigger-sftp"
              className="gap-2"
            >
              {syncLoading ? <Loader className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Sincronizar ahora
            </Button>
          </div>
        </div>

        {/* Drive fallback panel */}
        <DriveFallbackPanel onSyncDone={loadSyncLogs} />

        {/* Server IP Panel */}
        {serverIp && (
          <div className="mb-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 text-xs" data-testid="server-ip-panel">
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-sm flex items-center gap-1.5 text-blue-700 dark:text-blue-300">
                IP pública saliente del servidor de producción
              </span>
              <button onClick={() => setServerIp(null)} className="text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            </div>
            {serverIp.error ? (
              <p className="text-red-500">{serverIp.error}</p>
            ) : (
              <>
                <p className="text-muted-foreground mb-2">Proporciona esta IP al equipo IT para que la autoricen en el SFTP <code className="bg-muted px-1 rounded">{serverIp.sftp_target}</code></p>
                <div className="space-y-1">
                  {Object.entries(serverIp.outgoing_ips || {}).map(([svc, ip]) => (
                    <div key={svc} className="flex items-center gap-3">
                      <span className="text-muted-foreground w-40 flex-shrink-0">{svc}:</span>
                      <code className={`font-bold select-all ${ip.startsWith('error') ? 'text-orange-500' : 'text-green-600 dark:text-green-400'}`}>{ip}</code>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* SFTP Diagnostic Panel */}
        {sftpDiag && (
          <div className="mb-4 bg-muted/40 border border-border rounded-lg p-4 text-xs font-mono" data-testid="sftp-diag-panel">
            <div className="flex items-center justify-between mb-3">
              <span className="font-semibold text-sm font-sans flex items-center gap-1.5">
                <FolderOpen className="h-4 w-4" />
                Contenido del servidor SFTP
              </span>
              <button onClick={() => setSftpDiag(null)} className="text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            </div>

            {sftpDiag.error ? (
              <p className="text-red-500">{sftpDiag.error}</p>
            ) : (
              <>
                <p className="text-muted-foreground mb-1">Directorio: <span className="text-foreground">{sftpDiag.base_path || '(no configurado)'}</span></p>
                <p className="text-muted-foreground mb-3">Fichero activo: <span className={sftpDiag.configured_file_found ? 'text-green-500' : 'text-orange-400'}>{sftpDiag.configured_file || '(no configurado)'}</span>
                  {sftpDiag.configured_file_found && <span className="text-muted-foreground ml-2">— {sftpDiag.configured_file_row_count} filas</span>}
                </p>

                {sftpDiag.list_error ? (
                  <p className="text-orange-400 mb-2">Error al listar: {sftpDiag.list_error}</p>
                ) : (
                  <div className="space-y-1 max-h-64 overflow-y-auto">
                    <p className="text-muted-foreground font-sans font-semibold text-xs mb-2">{(sftpDiag.files || []).length} ficheros en el directorio:</p>
                    {(sftpDiag.files || []).map((f, i) => (
                      <div key={i} className={`flex justify-between gap-4 py-0.5 px-2 rounded ${f.name === sftpDiag.configured_file ? 'bg-primary/10 text-primary font-semibold' : 'text-foreground/80'}`}>
                        <span className="truncate">{f.name}</span>
                        <span className="flex-shrink-0 text-muted-foreground">{f.size_kb} KB</span>
                      </div>
                    ))}
                  </div>
                )}

                {sftpDiag.configured_file_found && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <p className="text-muted-foreground font-sans mb-1">Primera fila (cabecera):</p>
                    <p className="break-all text-foreground/70">{sftpDiag.configured_file_first_row}</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {syncLogs.length === 0 ? (
          <p className="text-sm text-muted-foreground">Sin registros de sincronización aún.</p>
        ) : (
          <div className="space-y-2">
            {syncLogs.map((log) => (
              <div key={log.id || `${log.date}-${log.source}`} className={`flex items-start gap-3 p-3 rounded-lg text-sm ${log.error ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800' : 'bg-muted/40 border border-border'}`}>
                <div className="mt-0.5 flex-shrink-0">
                  {log.error
                    ? <AlertCircle className="h-4 w-4 text-red-500" />
                    : <CheckCircle className="h-4 w-4 text-green-500" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{log.date}</span>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {log.created_at ? new Date(log.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : ''}
                    </span>
                  </div>
                  {log.error ? (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1 truncate">{log.error}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-1">
                      +{log.result?.created || 0} nuevos · {log.result?.updated || 0} actualizados · {log.result?.skipped || 0} omitidos
                      {(log.result?.deactivated || 0) > 0 && <span className="text-orange-600 ml-1">· {log.result.deactivated} bajas</span>}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Platform Settings */}
      {platformSettings && (
        <div className="bg-card border border-border rounded-xl p-5 shadow-sm" data-testid="platform-settings">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-semibold font-heading text-sm flex items-center gap-2">
                <Settings className="h-4 w-4 text-muted-foreground" />
                Configuración de la plataforma
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">Ajusta los límites de jornada y cuotas de teletrabajo</p>
            </div>
            <Button
              size="sm"
              onClick={handleSaveSettings}
              disabled={settingsSaving}
              className="bg-brand hover:bg-brand-hover text-white gap-2"
              data-testid="btn-save-settings"
            >
              {settingsSaving ? <Loader className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Guardar
            </Button>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { key: 'max_daily_hours', label: 'Horas máx. jornada', unit: 'h', min: 1, max: 24, step: 0.5 },
              { key: 'min_hours_warning', label: 'Alerta mín. jornada', unit: 'h', min: 1, max: 24, step: 0.5 },
              { key: 'max_weekly_remote_days', label: 'Días teletrabajo/semana', unit: 'd', min: 1, max: 7, step: 1 },
              { key: 'remote_days_per_month', label: 'Días teletrabajo/mes', unit: 'd', min: 1, max: 31, step: 1 },
              { key: 'vacation_days_per_year', label: 'Días vacaciones/año', unit: 'd', min: 1, max: 60, step: 1 },
              { key: 'low_balance_alert_threshold', label: 'Alerta saldo bajo (vacaciones)', unit: 'd', min: 0, max: 15, step: 1 },
            ].map(({ key, label, unit, min, max, step }) => (
              <div key={key} className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">{label}</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={min}
                    max={max}
                    step={step}
                    value={settingsEdit[key] ?? platformSettings[key]}
                    onChange={e => setSettingsEdit(prev => ({ ...prev, [key]: parseFloat(e.target.value) }))}
                    data-testid={`setting-${key}`}
                    className="w-full h-8 px-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-brand"
                  />
                  <span className="text-xs text-muted-foreground w-4">{unit}</span>
                </div>
              </div>
            ))}
          </div>
          {platformSettings.updated_at && (
            <p className="text-xs text-muted-foreground mt-3">
              Última actualización: {new Date(platformSettings.updated_at).toLocaleString('es-ES')} por {platformSettings.updated_by}
            </p>
          )}

          {/* Global rollout toggle */}
          <div className="mt-6 pt-4 border-t border-border">
            <div className="flex items-start justify-between gap-4 p-4 rounded-xl bg-gradient-to-br from-emerald-50 to-sky-50 dark:from-emerald-900/15 dark:to-sky-900/15 border border-emerald-200 dark:border-emerald-800">
              <div className="flex-1 min-w-0">
                <label className="text-sm font-semibold text-foreground">
                  🚀 Rollout global del módulo de Vacaciones
                </label>
                <p className="text-xs text-muted-foreground mt-1">
                  Cuando está activado, <strong>todos los empleados con calendario asignado</strong> ven el módulo de Vacaciones automáticamente
                  (sin necesidad de añadirlos al piloto). Cuando se desactiva, solo el piloto + el ancla recursiva tienen acceso.
                </p>
                <p className="text-[11px] text-muted-foreground mt-2 italic">
                  Estado actual:&nbsp;
                  {(settingsEdit.vacation_module_globally_enabled ?? platformSettings.vacation_module_globally_enabled) ? (
                    <span className="text-emerald-600 dark:text-emerald-400 font-semibold">ACTIVADO para toda la plantilla con calendario</span>
                  ) : (
                    <span className="text-orange-600 dark:text-orange-400 font-semibold">DESACTIVADO — solo el piloto tiene acceso</span>
                  )}
                </p>
              </div>
              <button
                type="button"
                data-testid="setting-vacation-global-toggle"
                onClick={() => setSettingsEdit(prev => ({
                  ...prev,
                  vacation_module_globally_enabled: !(
                    prev.vacation_module_globally_enabled ?? platformSettings.vacation_module_globally_enabled ?? false
                  ),
                }))}
                className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${
                  (settingsEdit.vacation_module_globally_enabled ?? platformSettings.vacation_module_globally_enabled)
                    ? 'bg-emerald-500'
                    : 'bg-slate-300 dark:bg-slate-600'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-md transition-transform mt-1 ${
                    (settingsEdit.vacation_module_globally_enabled ?? platformSettings.vacation_module_globally_enabled)
                      ? 'translate-x-6'
                      : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Vacation pilot */}
          <div className="mt-6 pt-4 border-t border-border space-y-2">
            <label className="text-xs font-medium text-muted-foreground">
              Piloto del módulo de vacaciones — email del responsable ancla
            </label>
            <p className="text-xs text-muted-foreground">
              Sólo este usuario y sus reportes (recursivos) tendrán activado el módulo de Vacaciones. Deja vacío para desactivarlo.
            </p>
            <div className="flex gap-2">
              <input
                type="email"
                placeholder="crubioc@valorizasm.com"
                value={settingsEdit.vacation_pilot_anchor_email ?? platformSettings.vacation_pilot_anchor_email ?? ''}
                onChange={e => setSettingsEdit(prev => ({ ...prev, vacation_pilot_anchor_email: e.target.value }))}
                data-testid="setting-vacation-pilot-email"
                className="flex-1 h-9 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-brand"
              />
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-border space-y-2">
            <label className="text-xs font-medium text-muted-foreground">
              Usuarios individuales del piloto (uno por línea)
            </label>
            <p className="text-xs text-muted-foreground">
              Personas concretas (fuera del árbol del ancla) que también verán el módulo de Vacaciones. Útil para casos puntuales.
            </p>
            <textarea
              rows={4}
              placeholder={"jpsanchez@valorizasm.com\nlperez@valorizasm.com"}
              value={(settingsEdit.vacation_pilot_extra_emails ?? platformSettings.vacation_pilot_extra_emails ?? []).join('\n')}
              onChange={e => setSettingsEdit(prev => ({
                ...prev,
                vacation_pilot_extra_emails: e.target.value
                  .split('\n')
                  .map(s => s.trim().toLowerCase())
                  .filter(Boolean),
              }))}
              data-testid="setting-vacation-pilot-extras"
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand"
            />
            <p className="text-[11px] text-muted-foreground">
              {(settingsEdit.vacation_pilot_extra_emails ?? platformSettings.vacation_pilot_extra_emails ?? []).length} emails individuales
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
